import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

const useUploadMessageImage = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (image: File) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["message-images"].$post({
            param: scope.param,
            form: { image },
          })
        : await client.merchant[":merchantId"]["message-images"].$post({
            param: scope.param,
            form: { image },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

type StorableImage = { file?: File; imageId?: string } | null | undefined;

export const useStoreComposerImage = () => {
  const { mutateAsync, isPending } = useUploadMessageImage();

  const storeWithUrl = useCallback(
    async (image: StorableImage): Promise<{ imageId: string; imageUrl?: string } | null> => {
      if (!image) return null;
      if (image.imageId) return { imageId: image.imageId };
      if (!image.file) return null;
      return await mutateAsync(image.file);
    },
    [mutateAsync],
  );

  const store = useCallback(
    async (image: StorableImage): Promise<string | null> => (await storeWithUrl(image))?.imageId ?? null,
    [storeWithUrl],
  );

  return { store, storeWithUrl, isPending };
};
